import { Address } from '@app/domain/Address'
import { Area } from '@app/domain/Area'
import { Booking } from '@app/domain/Booking'
import { BookingReason } from '@app/domain/BookingReason'
import { BookingSlot } from '@app/domain/BookingSlot'
import { Brand } from '@app/domain/Brand'
import { CancellationReason } from '@app/domain/CancellationReason'
import { Charge } from '@app/domain/Charge'
import { CommunicationPreferences } from '@app/domain/CommunicationPreferences'
import { DiaryPreferences } from '@app/domain/DiaryPreferences'
import { Event } from '@app/domain/VenueEvent'
import { ChargeRange } from '@app/domain/ChargeRange'
import { Helper } from '@app/helpers/utilities/helper'
import { Question, sourceTypeOfQuestion } from '@app/domain/Question'
import { SELECTABLE_SERVICE_STATUSES, ServiceStatus, isSelectableServiceStatus } from '@app/domain/ServiceStatus'
import { Table } from '@app/domain/Table'
import { VenueClosure } from '@app/domain/VenueClosure'
import { VenueRule } from '@app/domain/VenueRule'
import { VenueSchedule } from '@app/domain/VenueSchedule'
import { VenueVelocity } from '@app/domain/VenueVelocity'
import { WidgetConfiguration } from '@app/domain/WidgetConfiguration'
import { BookingAssignment } from '@app/domain/BookingAssignment'
import { OpeningHours } from '@app/domain/OpeningHours'
import shortUUID from 'short-uuid'
import { shouldShowVoucherVenueName, voucherDisplayName } from '../../../../shared/domain/voucher-display-name'

export type BookingSlotLimits = {
    bookingLimit: number | null,
    coverLimit: number | null,
    venueBookingLimit: number | null,
    venueCoverLimit: number | null,
    hasOverride: boolean,
}

export type BookingSlotLimitExcess = {
    bookingSlotStart: Date,
    bookingLimit: {
        currentBookings: number,
        nextBookings: number,
        limit: number,
    } | null,
    coverLimit: {
        currentCovers: number,
        nextCovers: number,
        limit: number,
    } | null,
}

export class Venue {

    voucherDisplayName(): string {
        return voucherDisplayName(this.id, this.displayName)
    }

    shouldShowVoucherVenueName(): boolean {
        return shouldShowVoucherVenueName(this.id)
    }

    constructor(
        public id: string,
        public displayName: string,
        public phoneNumber: string | null,
        public address: Address,
        public bookingInterval: number,
        public minPartySize: number | null,
        public minLargePartySize: number | null,
        public rules: VenueRule[],
        public largePartyMessage: string | null,
        public noBookingSlotAvailableMessage: string | null,
        public shortPreBookingWindowMessage: string | null,
        public dogFriendlyNoAvailabilityMessage: string | null,
        public bookingsPausedNoAvailabilityMessage: string | null,
        public venueClosedNoAvailabilityMessage: string | null,
        public timeZone: string,
        public cancellationChargeRanges: ChargeRange[],
        public depositRanges: ChargeRange[],
        public cancellationChargeCutOffHours: number,
        public cancellationChargeAutomatically: boolean,
        public communicationPreferences: CommunicationPreferences,
        public depositRefundCutOffDays: number | null,
        public depositRefundPolicy: string | null,
        public diaryPreferences: DiaryPreferences,
        public velocity: VenueVelocity,
        public widgetConfiguration: WidgetConfiguration,
        public areas: Area[],
        public customerCanChooseArea: boolean,
        /**
         * @deprecated Retained for backwards-compatible API round-tripping only.
         * Use customerCanChooseArea and reason.areaBookingOrder for allocation behaviour.
         */
        public areaBookingOrder: string[],
        public bookingsClosedUntilDate: Date | null,
        public brand: Brand,
        public bookingPolicy: string | null,
        public largePartyBookingPolicy: string | null,
        public largePartyBookingPolicyMinimumPartySize: number | null,
        public enquiriesEnabled: boolean,
        public waitlistsEnabled: boolean,
        public emailValidationEnabled: boolean,
        public autoExtendSeatedBookingEndTimes: boolean,
        public enabledServiceStatuses: ServiceStatus[],
        public voucherSoldEmailRecipients: string[] | null,
        public sendVoucherSoldMerchantEmail: boolean,
        public reasons: BookingReason[],
        public cancellationReasons: CancellationReason[],
        public questions: Question[],
        public events: Event[],
        public metaPixelId: string | null = null,
        public bookingAssignment: BookingAssignment = new BookingAssignment(),
        public description: string | null = null,
        public promotionalImageIds: string[] = [],
        public openingHours: OpeningHours | null = null,
        public privacyPolicy: string | null = null,
        public preorderMenuId: string | null = null
    ) { }

    removePromotionalImage(index: number) {
        this.promotionalImageIds.splice(index, 1)
    }

    get selectableEnabledServiceStatuses(): ServiceStatus[] {
        const enabledServiceStatuses = this.enabledServiceStatuses ?? []
        return SELECTABLE_SERVICE_STATUSES.filter(serviceStatus => enabledServiceStatuses.includes(serviceStatus))
    }

    get serviceStatusesEnabled(): boolean {
        return this.selectableEnabledServiceStatuses.length > 0
    }

    serviceStatusIsEnabled(serviceStatus: ServiceStatus | null | undefined): boolean {
        return isSelectableServiceStatus(serviceStatus) && this.selectableEnabledServiceStatuses.includes(serviceStatus)
    }

    get activeAreas(): Area[] {
        return this.areas.filter(area => area.isActive)
    }

    customerCanChooseAreaFor(reason: BookingReason | null): boolean {
        return !reason?.areaBookingOrder?.length && this.customerCanChooseArea
    }

    get areasSortedByDisplayOrder() {
        return [...this.areas].sort((a, b) => a.displayOrder - b.displayOrder)
    }

    get areasSortedByCalendarDisplayOrder(): Area[] {
        // Ideally there is a separate property here, distinct from area display order, mainly used for the customer side.

        // Add merchant area IDs here to show them first in the calendar only.
        // Areas within each group retain their configured display order.
        const areaIdsShownFirst: string[] = [
            '8bb643a5-3bcc-4d8b-9306-9e21e75248c4', // Canada Wood Dog Loungs
        ]
        return this.areasSortedByDisplayOrder.sort((a, b) => {
            return Number(areaIdsShownFirst.includes(b.id)) - Number(areaIdsShownFirst.includes(a.id))
        })
    }

    areaWithId(id: string): Area | null {
        return this.areas.find(a => a.id === id) || null
    }

    areaWithTableId(tableId: string): Area | null {
        return this.areas.find(area => area.getTableWithId(tableId) !== null) || null
    }

    areaWithTableCombinationId(combinationId: string): Area | null {
        return this.areas.find(area => {
            return area.combinations.find(c => c.id === combinationId) !== undefined
        }) || null
    }

    areaBookingIsIn(booking: Booking): Area | null {
        return this.areas.find(area => {
            return area.tablesUsedForBooking(booking).length > 0
        }) || null
    }

    updateArea(area: Area) {
        const index = this.areas.findIndex(a => a.id === area.id)
        this.areas[index] = area
    }

    removeArea(area: Area) {
        const index = this.areas.findIndex(a => a.id === area.id)
        this.areas.splice(index, 1)
    }

    get reasonsSortedByDisplayOrder(): BookingReason[] {
        return [...this.reasons].sort((a, b) => a.displayOrder - b.displayOrder)
    }

    reasonWithId(id: string): BookingReason | null {
        return this.reasons.find(r => r.id === id) || null
    }

    addReason(reason: BookingReason) {
        if (this.reasonWithId(reason.id) !== null) {
            return
        }
        this.reasons.push(reason)
    }

    updateReason(reason: BookingReason) {
        const index = this.reasons.findIndex(r => r.id === reason.id)
        if (index === -1) {
            return
        }
        this.reasons[index] = reason
    }

    removeReason(reason: BookingReason) {
        const index = this.reasons.findIndex(r => r.id === reason.id)
        if (index === -1) {
            return
        }
        this.reasons.splice(index, 1)
        this.areas.forEach(area => {
            area.tables.forEach(table => {
                table.removeReason(reason)
            })
        })
    }

    get cancellationReasonsSortedByOrderIndex(): CancellationReason[] {
        return [...this.cancellationReasons]
            .sort((a, b) => a.orderIndex - b.orderIndex)
    }

    cancellationReasonWithId(id: string): CancellationReason | null {
        return this.cancellationReasons.find(cr => cr.id === id) || null
    }

    addCancellationReason(cancellationReason: CancellationReason) {
        if (this.cancellationReasonWithId(cancellationReason.id) !== null) {
            return
        }
        this.cancellationReasons.push(cancellationReason)
    }

    updateCancellationReason(cancellationReason: CancellationReason) {
        const index = this.cancellationReasons.findIndex(cr => cr.id === cancellationReason.id)
        if (index === -1) {
            return
        }
        this.cancellationReasons[index] = cancellationReason
    }

    removeCancellationReason(cancellationReason: CancellationReason) {
        const index = this.cancellationReasons.findIndex(cr => cr.id === cancellationReason.id)
        if (index === -1) {
            return
        }
        this.cancellationReasons.splice(index, 1)
    }

    get eventsSortedByDisplayOrder(): Event[] {
        return [...this.events].sort((a, b) => a.displayOrder - b.displayOrder)
    }

    get pinnedEvent(): Event | null {
        return this.events.find(e => e.id === this.widgetConfiguration.pinnedEventId) || null
    }

    eventWithId(id: string): Event | null {
        return this.events.find(e => e.id === id) || null
    }

    addEvent(event: Event) {
        if (this.eventWithId(event.id) !== null) {
            return
        }
        this.events.push(event)
    }

    updateEvent(event: Event) {
        const index = this.events.findIndex(e => e.id === event.id)
        if (index === -1) {
            return
        }
        this.events[index] = event
    }

    removeEvent(event: Event) {
        const index = this.events.findIndex(e => e.id === event.id)
        if (index === -1) {
            return
        }
        this.events.splice(index, 1)
    }

    addQuestion(question: Question) {
        if (this.questionWithId(question.id) !== null) {
            return
        }
        this.questions.push(question)
    }

    updateQuestion(question: Question) {
        const index = this.questions.findIndex(q => q.id === question.id)
        if (index === -1) {
            return
        }
        this.questions[index] = question
    }

    removeQuestion(question: Question) {
        const index = this.questions.findIndex(q => q.id === question.id)
        if (index === -1) {
            return
        }
        this.questions.splice(index, 1)
    }

    questionWithId(id: string): Question | null {
        return this.questions.find(q => q.id === id) || null
    }

    get hasMerchantQuestions(): boolean {
        return this.questions
            .filter(question => sourceTypeOfQuestion(question) === 'merchant')
            .length > 0
    }

    get hasSystemDefinedQuestions(): boolean {
        return this.questions
            .filter(question => sourceTypeOfQuestion(question) === 'system-defined')
            .length > 0
    }

    tablesUsingReason(reason: BookingReason): Table[] {
        return this.areas
            .map(area => area.tablesReservableForReasonOrNone(reason))
            .flat()
    }

    reservableTablesUsingNoReason(): Table[] {
        if (this.reasons.length === 0) {
            return []
        }
        return this.areas
            .map(area => area.tablesReservableForReasonOrNone(null))
            .flat()
    }

    tablesAssignedToEvent(event: Event): Table[] {
        return this.areas
            .map(area => area.tables)
            .flat()
            .filter(table => event.tableIds.some(ids => ids.includes(table.id)))
    }

    reasonsThatQuestionIsLimitedTo(question: Question): BookingReason[] {
        return this.reasons.filter(reason => question.reasonIds.includes(reason.id))
    }

    questionsAskedForEvent(event: Event): Question[] {
        return this.questions.filter(question => event.questionIds.includes(question.id))
    }

    reasonsReservableOnDate(date: Date): BookingReason[] {
        return this.reasons.filter(reason => {
            return reason.isActive && this.areas.some(area => {
                return area.tablesReservableForReasonOrNoneOnDate(reason, date, this.timeZone).length > 0
            })
        })
    }

    reasonsReservableInAreaOnDate(area: Area, dateTime: Date): BookingReason[] {
        return this.reasons.filter(reason => {
            return reason.isActive && area.tablesReservableForReasonOrNoneOnDate(reason, dateTime, this.timeZone).length > 0
        })
    }

    eventsReservableOnDate(date: Date): Event[] {
        return this.events.filter(event => {
            return event.recurrence.appliesToDate(date)
        })
    }

    cancellationChargeForPartySize(reasonId: string | null, eventId: string | null, partySize: number, dateTime: Date): Charge | null {
        if (reasonId !== null) {
            const reason = this.reasonWithId(reasonId)
            if (reason !== null) {
                const charge = reason.cancellationChargeForPartySize(partySize)
                if (charge !== null) {
                    return charge
                }
            }
        }
        if (eventId !== null) {
            const event = this.eventWithId(eventId)
            if (event === null) {
                return null
            }
            return event.cancellationChargeForPartySize(partySize)
        }
        const applicableRange = this.cancellationChargeRanges.find(range => {
            return range.isApplicable(partySize, dateTime, this.timeZone)
        })

        if (!applicableRange || applicableRange.unitAmount === null) {
            return null
        }
        return new Charge(
            partySize,
            applicableRange.minPartySize,
            applicableRange.maxPartySize,
            applicableRange.unitAmount,
            applicableRange.isPerCover
        )
    }

    bookingSlotClosestToDate(
        date: Date,
        startDate: Date,
        endDate: Date,
        dateIntervalsFactory: (startDate: Date, endDate: Date, interval: number) => Date[]
    ): BookingSlot | null {
        const bookingSlots = this.bookingSlotsBetweenDates(
            startDate,
            endDate,
            dateIntervalsFactory
        )
        const closestBookingSlot = bookingSlots.find(bookingSlot => {
            return Helper.isBetweenDate(
                date,
                bookingSlot.dateTime,
                bookingSlot.endDateTime(this.bookingInterval)
            )
        })
        if (!closestBookingSlot) {
            return null
        }
        return closestBookingSlot
    }

    bookingSlotsBetweenDates(
        startDate: Date,
        endDate: Date,
        dateIntervalsFactory: (startDate: Date, endDate: Date, interval: number) => Date[]
    ): BookingSlot[] {
        const intervalDates = dateIntervalsFactory(
            startDate,
            endDate,
            this.bookingInterval
        )
        return intervalDates.map(intervalDate => new BookingSlot(intervalDate))
    }

    hasDepositRefundCutOffPassed(booking: Booking): boolean {
        const cutOffDate = this.depositRefundCutOffDate(booking)
        if (cutOffDate === null) {
            return true
        }
        const now = new Date()
        return now.getTime() > cutOffDate.getTime()
    }

    depositRefundCutOffDate(booking: Booking): Date | null {
        const cutOffDays = this.depositRefundCutOffDaysForBooking(booking)
        if (cutOffDays === null) {
            return null
        }
        const bookingStart = new Date(booking.start)
        return new Date(
            bookingStart.getTime() - (cutOffDays * 24 * 60 * 60 * 1000)
        )
    }

    isSendFeedbackRequestEnabled(): boolean {
        return this.communicationPreferences.sendFeedbackRequest
    }

    tableGroupsInClosure(closure: VenueClosure, date: Date): Table[][] {
        if (closure.appliesToEntireVenue) {
            return [[]]
        }
        return this.contiguouslyDisplayedTablesInListOnDate(closure.tableIds, date)
    }

    closestEdgeToEndBookingAt(now: Date, bookingSlot: BookingSlot, booking: Booking) {
        const bookingSlotEdge = bookingSlot.closestEdgeToDateTime(now, this.bookingInterval)
        const earliestEnd = new BookingSlot(booking.start).endDateTime(this.bookingInterval)
        return bookingSlotEdge < earliestEnd ? earliestEnd : bookingSlotEdge
    }

    removeBrandBannerImage() {
        this.brand.bannerImageId = null
    }

    removeVoucherLogoImage() {
        this.brand.voucherLogoImageId = null
    }

    checkIfIdIsBierHalle() {
        //temporary implementation to show the kitchen cover velocity input
        //check if venue id is Bier Halle
        return this.id === '8025acaf-1c0d-4695-86d8-65804d890a4b'
    }

    getLandingPageURL(
        baseUrl: string
    ): string {
        const translator = shortUUID()
        const base58VenueId = translator.fromUUID(this.id)
        return baseUrl + '/' + base58VenueId
    }

    hasANonDogFriendlyTable(): boolean {
        return this.areas.some(area => area.tables.some(table => table.dogFriendly === false))
    }

    hasANonWheelchairAccessibleTable(): boolean {
        return this.areas.some(area => area.tables.some(table => table.wheelchairAccessible === false))
    }

    hasANonPramAccessibleTable(): boolean {
        return this.areas.some(area => area.tables.some(table => table.pramAccessible === false))
    }

    hasANonHighchairFriendlyTable(): boolean {
        return this.areas.some(area => area.tables.some(table => table.highchairFriendly === false))
    }

    depositChargeForPartySize(reasonId: string | null, eventId: string | null, partySize: number, dateTime: Date): Charge | null {
        if (reasonId !== null) {
            const reason = this.reasonWithId(reasonId)
            if (reason !== null) {
                const charge = reason.depositChargeForPartySize(partySize)
                if (charge !== null) {
                    return charge
                }
            }
        }
        if (eventId !== null) {
            const event = this.eventWithId(eventId)
            if (event === null) {
                return null
            }
            return event.depositChargeForPartySize(partySize)
        }

        const applicableRange = this.depositRanges.find(range => {
            return range.isApplicable(partySize, dateTime, this.timeZone)
        })

        if (!applicableRange || applicableRange.unitAmount === null) {
            return null
        }

        return new Charge(
            partySize,
            applicableRange.minPartySize,
            applicableRange.maxPartySize,
            applicableRange.unitAmount,
            applicableRange.isPerCover
        )
    }

    areAllTablesOccupiedDuringBookingSlot(
        bookingSlot: BookingSlot,
        otherBookings: Booking[]
    ): boolean {
        const bookingStart = bookingSlot.dateTime
        const bookingEnd = bookingSlot.endDateTime(this.bookingInterval)
        return this.areas.every(area => {
            return area.availableTables.every(table => {
                return otherBookings
                    .filter(booking => booking.isActive())
                    .some(booking => {
                        return booking.occupiesTableBetweenDates(bookingStart, bookingEnd, [table])
                    })
            })
        })
    }

    bookingLimitAtDateTime(dateTime: Date): number | null {
        return this.velocity.bookingLimitAtDateTime(dateTime, this.timeZone)
    }

    coverLimitAtDateTime(dateTime: Date): number | null {
        return this.velocity.coverLimitAtDateTime(dateTime, this.timeZone)
    }

    bookingSlotLimitsAtDateTime(dateTime: Date, venueSchedule?: VenueSchedule | null): BookingSlotLimits {
        const venueBookingLimit = this.bookingLimitAtDateTime(dateTime)
        const venueCoverLimit = this.coverLimitAtDateTime(dateTime)
        const override = venueSchedule?.bookingSlotVelocityLimitOverrides.find(limitOverride => {
            return limitOverride.startDateTime.getTime() === dateTime.getTime()
        }) ?? null
        return {
            bookingLimit: override?.bookingLimit ?? venueBookingLimit,
            coverLimit: override?.coverLimit ?? venueCoverLimit,
            venueBookingLimit,
            venueCoverLimit,
            hasOverride: override !== null,
        }
    }

    bookingSlotLimitExcessIfAddingBooking(
        dateTime: Date,
        partySize: number,
        bookings: Booking[],
        venueSchedule?: VenueSchedule | null
    ): BookingSlotLimitExcess | null {
        const bookingSlot = new BookingSlot(dateTime)
        const bookingSlotEnd = bookingSlot.endDateTime(this.bookingInterval)
        const bookingsInSlot = bookings
            .filter(booking => booking.isActive())
            .filter(booking => {
                return booking.startsBetweenDates(
                    bookingSlot.dateTime,
                    bookingSlotEnd
                )
            })
        const currentBookings = bookingsInSlot.length
        const currentCovers = bookingsInSlot.reduce((total, booking) => {
            return total + booking.size
        }, 0)
        const nextBookings = currentBookings + 1
        const nextCovers = currentCovers + partySize
        const limits = this.bookingSlotLimitsAtDateTime(bookingSlot.dateTime, venueSchedule)
        const bookingLimit = limits.bookingLimit !== null && nextBookings > limits.bookingLimit
            ? {
                currentBookings,
                nextBookings,
                limit: limits.bookingLimit,
            }
            : null
        const coverLimit = limits.coverLimit !== null && nextCovers > limits.coverLimit
            ? {
                currentCovers,
                nextCovers,
                limit: limits.coverLimit,
            }
            : null
        if (bookingLimit === null && coverLimit === null) {
            return null
        }
        return {
            bookingSlotStart: bookingSlot.dateTime,
            bookingLimit,
            coverLimit,
        }
    }

    canBeBookedWithManualAcceptance(): boolean {
        const anyAreaHasManualAcceptance = this.areas.some(area => area.scheduledForManualAcceptance)
        if (anyAreaHasManualAcceptance) {
            return true
        }
        const anyEventHasManualAcceptance = this.events.some(event => event.manualAcceptance)
        if (anyEventHasManualAcceptance) {
            return true
        }
        return false
    }

    private depositRefundCutOffDaysForBooking(booking: Booking): number | null {
        if (booking.deposit?.paymentMethodId === null) {
            return null
        }
        if (booking.eventId === null) {
            return this.depositRefundCutOffDays
        }
        const event = this.eventWithId(booking.eventId)
        return event?.depositRefundCutOffDays || null
    }

    private contiguouslyDisplayedTablesInListOnDate(tableIds: string[], date: Date): Table[][] {
        return this.areasSortedByCalendarDisplayOrder
            .map(area => {
                return area.contiguouslyDisplayedTablesInListOnDate(tableIds, date)
            })
            .filter(tables => tables.length > 0)
            .flat()
    }
}
